/**
 * Domain and URL validation utilities
 * Validates various formats of domains and URLs according to web standards
 */

interface IValidationResult {
    isValid: boolean;
    type: "domain" | "url" | "invalid";
    error?: string;
}

/**
 * Validates domain-only strings (without protocol)
 */
function validateDomainOnly(domain: string): IValidationResult {
    if (!domain.trim()) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain cannot be empty",
        };
    }

    // Check for invalid characters (allow alphanumeric, dots, and hyphens)
    if (!/^[a-zA-Z0-9.-]+$/.test(domain)) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain contains invalid characters",
        };
    }

    // Check for consecutive dots
    if (domain.includes("..")) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain cannot contain consecutive dots",
        };
    }

    // Check for trailing dot
    if (domain.endsWith(".")) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain cannot end with a dot",
        };
    }

    // Check for starting or ending with hyphen
    if (domain.startsWith("-") || domain.endsWith("-")) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain cannot start or end with a hyphen",
        };
    }

    // Check for starting with dot
    if (domain.startsWith(".")) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain cannot start with a dot",
        };
    }

    // Split into parts and validate each
    const parts = domain.split(".");

    // Must have at least 2 parts (domain.tld)
    if (parts.length < 2) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain must have at least one dot (e.g., domain.com)",
        };
    }

    // Check total domain length (max 253 characters)
    if (domain.length > 253) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain cannot exceed 253 characters",
        };
    }

    // Validate each part
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];

        // Check for empty parts
        if (!part) {
            return {
                isValid: false,
                type: "invalid",
                error: "Domain parts cannot be empty",
            };
        }

        // Check part length (max 63 characters per label)
        if (part.length > 63) {
            return {
                isValid: false,
                type: "invalid",
                error: "Domain parts cannot exceed 63 characters",
            };
        }

        // Check for hyphens at start/end of parts
        if (part.startsWith("-") || part.endsWith("-")) {
            return {
                isValid: false,
                type: "invalid",
                error: "Domain parts cannot start or end with hyphens",
            };
        }

        // TLD validation (last part)
        if (i === parts.length - 1) {
            // TLD must be at least 2 characters and contain only letters
            if (part.length < 2 || !/^[a-zA-Z]+$/.test(part)) {
                return {
                    isValid: false,
                    type: "invalid",
                    error: "Top-level domain must be at least 2 letters and contain only alphabetic characters",
                };
            }
        }
    }

    return {
        isValid: true,
        type: "domain",
    };
}

/**
 * Validates if a string is a valid URL
 */
export function validateURL(url: string): IValidationResult {
    if (!url || typeof url !== "string") {
        return {
            isValid: false,
            type: "invalid",
            error: "URL must be a non-empty string",
        };
    }

    const trimmedUrl = url.trim();

    // Check for valid protocols (only http and https)
    if (!/^https?:\/\//.test(trimmedUrl)) {
        return {
            isValid: false,
            type: "invalid",
            error: "URL must start with http:// or https://",
        };
    }

    try {
        const urlObj = new URL(trimmedUrl);

        // Check for invalid protocols
        if (!["http:", "https:"].includes(urlObj.protocol)) {
            return {
                isValid: false,
                type: "invalid",
                error: "Only HTTP and HTTPS protocols are allowed",
            };
        }

        // Validate the hostname part
        const domainValidation = validateDomainOnly(urlObj.hostname);
        if (!domainValidation.isValid) {
            return {
                isValid: false,
                type: "invalid",
                error: `Invalid domain in URL: ${domainValidation.error}`,
            };
        }

        return {
            isValid: true,
            type: "url",
        };
    } catch {
        return {
            isValid: false,
            type: "invalid",
            error: "Invalid URL format",
        };
    }
}

/**
 * Validates if a string is a valid domain name
 */
export function validateDomain(domain: string): IValidationResult {
    if (!domain || typeof domain !== "string") {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain must be a non-empty string",
        };
    }

    const trimmedDomain = domain.trim();

    if (!trimmedDomain) {
        return {
            isValid: false,
            type: "invalid",
            error: "Domain cannot be empty or whitespace only",
        };
    }

    // Check if it's a URL (has protocol)
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmedDomain)) {
        return validateURL(trimmedDomain);
    }

    // Domain-specific validation
    return validateDomainOnly(trimmedDomain);
}

/**
 * Simple validation function that returns boolean
 */
export function isValidDomainOrURL(input: string): boolean {
    return validateDomain(input).isValid;
}

/**
 * Extracts domain from URL or returns the input if it's already a domain
 */
export function extractDomain(input: string): string {
    if (!input) {
        return "";
    }

    const trimmedInput = input.trim();
    if (!trimmedInput) {
        return "";
    }

    try {
        // Try to parse as URL first
        if (/^https?:\/\//.test(trimmedInput)) {
            const url = new URL(trimmedInput);
            return url.hostname;
        }
        return trimmedInput;
    } catch {
        return trimmedInput;
    }
}

/**
 * Extracts the path from a URL
 */
function extractPath(input: string): string {
    if (!input) {
        return "";
    }

    const trimmedInput = input.trim();
    if (!trimmedInput) {
        return "";
    }

    try {
        if (/^https?:\/\//.test(trimmedInput)) {
            const url = new URL(trimmedInput);
            return url.pathname;
        }
        return "";
    } catch {
        return "";
    }
}

/**
 * Extracts query parameters from a URL
 */
function extractQueryParams(input: string): Record<string, string> {
    if (!input) {
        return {};
    }

    const trimmedInput = input.trim();
    if (!trimmedInput) {
        return {};
    }

    try {
        if (/^https?:\/\//.test(trimmedInput)) {
            const url = new URL(trimmedInput);
            const params: Record<string, string> = {};

            url.searchParams.forEach((value, key) => {
                params[key] = value;
            });

            return params;
        }
        return {};
    } catch {
        return {};
    }
}

/**
 * Extracts the fragment (hash) from a URL
 */
function extractFragment(input: string): string {
    if (!input) {
        return "";
    }

    const trimmedInput = input.trim();
    if (!trimmedInput) {
        return "";
    }

    try {
        if (/^https?:\/\//.test(trimmedInput)) {
            const url = new URL(trimmedInput);
            return url.hash ? url.hash.substring(1) : ""; // Remove the # symbol
        }
        return "";
    } catch {
        return "";
    }
}

/**
 * Extracts the port from a URL
 */
function extractPort(input: string): string {
    if (!input) {
        return "";
    }

    const trimmedInput = input.trim();
    if (!trimmedInput) {
        return "";
    }

    try {
        if (/^https?:\/\//.test(trimmedInput)) {
            const url = new URL(trimmedInput);
            return url.port;
        }
        return "";
    } catch {
        return "";
    }
}

/**
 * Parses a URL into its components
 */
function parseURL(input: string): {
    isValid: boolean;
    protocol?: string;
    hostname?: string;
    port?: string;
    pathname?: string;
    search?: string;
    hash?: string;
    queryParams?: Record<string, string>;
} {
    if (!input) {
        return { isValid: false };
    }

    const trimmedInput = input.trim();
    if (!trimmedInput) {
        return { isValid: false };
    }

    try {
        if (/^https?:\/\//.test(trimmedInput)) {
            const url = new URL(trimmedInput);
            const queryParams: Record<string, string> = {};

            url.searchParams.forEach((value, key) => {
                queryParams[key] = value;
            });

            return {
                isValid: true,
                protocol: url.protocol,
                hostname: url.hostname,
                port: url.port,
                pathname: url.pathname,
                search: url.search,
                hash: url.hash,
                queryParams,
            };
        }

        return { isValid: false };
    } catch {
        return { isValid: false };
    }
}
